/**
 * Educational content and tooltips for technical color concepts
 * Provides contextual help throughout the application
 */

export interface EducationalContent {
  title: string;
  description: string;
  learnMoreUrl?: string;
  category: 'color-space' | 'print' | 'accessibility' | 'conversion' | 'general';
}

export const educationalContent: Record<string, EducationalContent> = {
  // Color Space Explanations
  oklch: {
    title: 'OKLCH Color Space',
    description: 'OKLCH (Lightness, Chroma, Hue) is a perceptually uniform color space based on human vision. It produces smoother gradients and more predictable color adjustments than HSL. Best for UI design and color harmony generation.',
    learnMoreUrl: 'https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/oklch',
    category: 'color-space',
  },
  lab: {
    title: 'CIE Lab Color Space',
    description: 'Lab is a device-independent color space that represents all perceivable colors. L* represents lightness (0-100), a* represents green-red axis, and b* represents blue-yellow axis. Widely used in print and color science.',
    learnMoreUrl: 'https://en.wikipedia.org/wiki/CIELAB_color_space',
    category: 'color-space',
  },
  hsl: {
    title: 'HSL Color Space',
    description: 'HSL (Hue, Saturation, Lightness) is intuitive for designers but not perceptually uniform. Colors with the same lightness value may appear different brightness to the human eye. Consider OKLCH for more accurate results.',
    learnMoreUrl: 'https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/hsl',
    category: 'color-space',
  },
  cmyk: {
    title: 'CMYK Color Space',
    description: 'CMYK (Cyan, Magenta, Yellow, Key/Black) is a subtractive color model used in printing. Converting from RGB to CMYK is lossy and depends on the ICC profile used. Always work in CMYK from the start for print projects.',
    learnMoreUrl: 'https://www.color.org/',
    category: 'color-space',
  },
  displayP3: {
    title: 'Display P3 Color Space',
    description: 'Display P3 is a wide-gamut color space used by modern displays (Apple devices, high-end monitors). It covers ~25% more colors than sRGB, particularly vibrant greens and reds. Ensure fallbacks for older displays.',
    learnMoreUrl: 'https://webkit.org/blog/10042/wide-gamut-color-in-css-with-display-p3/',
    category: 'color-space',
  },

  // Print & ICC Profile Concepts
  whyCmykChanged: {
    title: 'Why Did My CMYK Change?',
    description: 'RGB to CMYK conversion is irreversible and profile-dependent. Different ICC profiles (FOGRA, GRACoL) produce different CMYK values for the same RGB color. Out-of-gamut colors are clipped or compressed. Always specify your target profile and rendering intent.',
    learnMoreUrl: 'https://www.updig.org/guidelines/',
    category: 'print',
  },
  renderingIntentPerceptual: {
    title: 'Perceptual Rendering Intent',
    description: 'Preserves the visual relationship between colors by compressing the entire color gamut. Best for photographs and images with many out-of-gamut colors. May shift all colors slightly to maintain relative appearance.',
    learnMoreUrl: 'https://www.color.org/rendering_intents.xalter',
    category: 'print',
  },
  renderingIntentRelative: {
    title: 'Relative Colorimetric Intent',
    description: 'Preserves in-gamut colors exactly and clips out-of-gamut colors to the nearest reproducible color. Paper white is mapped to the destination white point. Best for logos and spot colors where accuracy matters.',
    learnMoreUrl: 'https://www.color.org/rendering_intents.xalter',
    category: 'print',
  },
  renderingIntentAbsolute: {
    title: 'Absolute Colorimetric Intent',
    description: 'Simulates the exact appearance including paper color. Used for proofing workflows where you need to preview how colors will look on a specific substrate. Does not adjust for white point differences.',
    learnMoreUrl: 'https://www.color.org/rendering_intents.xalter',
    category: 'print',
  },
  renderingIntentSaturation: {
    title: 'Saturation Rendering Intent',
    description: 'Maximizes color vividness at the expense of accuracy. Best for business graphics, charts, and presentations where vibrant colors are more important than precise color matching.',
    learnMoreUrl: 'https://www.color.org/rendering_intents.xalter',
    category: 'print',
  },
  iccProfile: {
    title: 'ICC Profiles',
    description: 'ICC profiles define how colors are interpreted for specific devices or standards. Common profiles include FOGRA39 (European coated paper), GRACoL (US coated), and SWOP (US newsprint). Always use the profile matching your print specification.',
    learnMoreUrl: 'https://www.color.org/icc_specs2.xalter',
    category: 'print',
  },
  fogra: {
    title: 'FOGRA Standards',
    description: 'FOGRA is a European research institute that defines printing standards. FOGRA39 (ISO Coated v2) is the most common for coated paper in Europe. FOGRA51 is the newer standard. Download profiles from fogra.org.',
    learnMoreUrl: 'https://www.fogra.org/',
    category: 'print',
  },
  gracol: {
    title: 'GRACoL Standard',
    description: 'GRACoL (General Requirements for Applications in Commercial Offset Lithography) is the US standard for coated paper printing. GRACoL2006_Coated1v2 is widely used. Download from idealliance.org.',
    learnMoreUrl: 'https://www.idealliance.org/',
    category: 'print',
  },
  tac: {
    title: 'Total Area Coverage (TAC)',
    description: 'TAC is the sum of all CMYK ink percentages. High TAC (>300%) can cause drying issues, smudging, or paper warping. Industry standards: Coated paper 300-330%, Uncoated 260-280%, Newsprint 240%. Always check with your printer.',
    learnMoreUrl: 'https://en.wikipedia.org/wiki/Ink_limit',
    category: 'print',
  },
  gamutWarning: {
    title: 'Gamut Warning',
    description: 'Colors outside the reproducible range of the target color space. Out-of-gamut colors will be clipped or compressed during conversion, potentially losing detail or shifting hue. Use soft proofing to preview the result.',
    learnMoreUrl: 'https://www.color.org/gamut.xalter',
    category: 'print',
  },
  softProofing: {
    title: 'Soft Proofing',
    description: 'Simulates how colors will appear when printed using a specific ICC profile and rendering intent. Shows color shifts and gamut clipping before physical printing. Essential for accurate print workflows.',
    learnMoreUrl: 'https://www.color.org/softproofing.xalter',
    category: 'print',
  },
  blackPointCompensation: {
    title: 'Black Point Compensation',
    description: 'Adjusts the darkest colors to match the dynamic range of the destination. Prevents shadow detail loss when converting between profiles with different black points. Generally recommended to enable.',
    learnMoreUrl: 'https://www.color.org/bpc.xalter',
    category: 'print',
  },

  // Accessibility & Delta-E Metrics
  deltaE76: {
    title: 'ΔE76 (CIE76)',
    description: 'The original color difference formula based on Euclidean distance in Lab space. Simple but not perceptually uniform—doesn\'t match human vision well. ΔE76 < 2.3 is considered just noticeable. Use ΔE2000 for better accuracy.',
    learnMoreUrl: 'https://en.wikipedia.org/wiki/Color_difference#CIE76',
    category: 'accessibility',
  },
  deltaE94: {
    title: 'ΔE94 (CIE94)',
    description: 'Improved formula that weights lightness, chroma, and hue differently. Better than ΔE76 but still has limitations. ΔE94 < 1 is imperceptible, 1-2 is just noticeable, >2 is noticeable. Superseded by ΔE2000.',
    learnMoreUrl: 'https://en.wikipedia.org/wiki/Color_difference#CIE94',
    category: 'accessibility',
  },
  deltaE2000: {
    title: 'ΔE2000 (CIEDE2000)',
    description: 'The most accurate color difference formula, closely matching human perception. Accounts for lightness, chroma, hue, and their interactions. ΔE2000 < 1 is imperceptible, 1-2 is just noticeable, 2-10 is noticeable, >10 is very different. Industry standard.',
    learnMoreUrl: 'https://en.wikipedia.org/wiki/Color_difference#CIEDE2000',
    category: 'accessibility',
  },
  wcagAA: {
    title: 'WCAG 2.2 Level AA',
    description: 'Minimum contrast ratio of 4.5:1 for normal text (under 18pt or 14pt bold) and 3:1 for large text. Required for legal compliance in many jurisdictions. Ensures readability for users with low vision.',
    learnMoreUrl: 'https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html',
    category: 'accessibility',
  },
  wcagAAA: {
    title: 'WCAG 2.2 Level AAA',
    description: 'Enhanced contrast ratio of 7:1 for normal text and 4.5:1 for large text. Provides better readability for users with moderate low vision. Recommended for critical interfaces and text-heavy applications.',
    learnMoreUrl: 'https://www.w3.org/WAI/WCAG22/Understanding/contrast-enhanced.html',
    category: 'accessibility',
  },
  colorBlindness: {
    title: 'Color Blindness Simulation',
    description: 'Simulates how colors appear to people with different types of color vision deficiency. Protanopia (red-blind), Deuteranopia (green-blind), and Tritanopia (blue-blind) affect ~8% of males and ~0.5% of females. Never rely on color alone to convey information.',
    learnMoreUrl: 'https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html',
    category: 'accessibility',
  },

  // Conversion & General Concepts
  gamutClamping: {
    title: 'Gamut Clamping',
    description: 'Forces out-of-gamut colors to the nearest reproducible color within the target color space. May cause banding or hue shifts. Alternative: use perceptual rendering intent for smoother compression.',
    learnMoreUrl: 'https://www.color.org/gamut.xalter',
    category: 'conversion',
  },
  perceptualUniformity: {
    title: 'Perceptual Uniformity',
    description: 'A color space is perceptually uniform when equal distances in the space correspond to equal perceived color differences. OKLCH and Lab are perceptually uniform; RGB and HSL are not. Use uniform spaces for gradients and color manipulation.',
    learnMoreUrl: 'https://en.wikipedia.org/wiki/Color_space#Perceptually_uniform_color_spaces',
    category: 'conversion',
  },
  alphaBlending: {
    title: 'Alpha Blending',
    description: 'Combines foreground and background colors based on the alpha (opacity) value. Formula: result = (fg × alpha) + (bg × (1 - alpha)). Important for accurate contrast calculations with transparent colors.',
    learnMoreUrl: 'https://en.wikipedia.org/wiki/Alpha_compositing',
    category: 'conversion',
  },
  illuminant: {
    title: 'Standard Illuminant',
    description: 'The light source used for color measurement. D65 (daylight, 6500K) is standard for displays. D50 (5000K) is standard for print viewing. Colors appear different under different illuminants—this is called metamerism.',
    learnMoreUrl: 'https://en.wikipedia.org/wiki/Standard_illuminant',
    category: 'general',
  },
};

/**
 * Get educational content by key
 */
export function getEducationalContent(key: string): EducationalContent | null {
  return educationalContent[key] || null;
}

/**
 * Get all content for a specific category
 */
export function getContentByCategory(category: EducationalContent['category']): EducationalContent[] {
  return Object.values(educationalContent).filter((content) => content.category === category);
}

/**
 * Search educational content by keyword
 */
export function searchEducationalContent(query: string): EducationalContent[] {
  const lowerQuery = query.toLowerCase();
  return Object.values(educationalContent).filter(
    (content) =>
      content.title.toLowerCase().includes(lowerQuery) ||
      content.description.toLowerCase().includes(lowerQuery)
  );
}
